import React, { useEffect, useRef, useState } from 'react';
import {
  ComboBoxParam,
  Form,
  Row,
  SonForm,
  SubmitButton,
  Textarea,
  TextAreaParam,
  useForm,
} from '../../../../_lib/Form2';
import { useSonTranslation } from '../../../../translation/useSonTranslation';
import {
  ListBoxModifier,
  PersonPreview,
  TopUserResult,
  UserSelectComboBox,
} from '../../../../components/molecules/Form/components/ComboBox';
import { FormInputModifier } from '../../../../components/molecules/Form/components/FormInput/FormInput.modifier';
import { useHtmlValidId } from '../../../../hooks/useHtmlValidId';
import { HiddenInput } from '../../../../_lib/Form2/elements/HiddenInput/HiddenInput';
import { TooltipPlacement } from '../../../../components/atoms/Tooltip';
import { sendLiveRegionMessage } from '../../../../components/atoms/LiveRegionGlobal/LiveRegionGlobal';
import { useDelayed } from '../../../../hooks/useDelayed';

export type PageInteractionsRecommendationsProps = {
  form: SonForm<ComboBoxParam<'recipients'> | TextAreaParam<'message'>>;
  closeCollapsible: () => void;
};

export function PageInteractionsRecommendationsForm({ form, closeCollapsible }: PageInteractionsRecommendationsProps) {
  const { t } = useSonTranslation('PageInteractionsRecommendations');
  const { formProps, fetchState, params } = useForm({ form, onSuccess: useDelayed(closeCollapsible) });
  const [selectedUsers, setSelectedUsers] = useState<TopUserResult[]>([]);
  const formRef = useRef<HTMLFormElement>(null);
  const recipients = params.getByName('recipients');
  const message = params.getByName('message');
  const comboboxId = `recommendation-combobox-${useHtmlValidId()}`;

  // set focus on combobox input after deleting a user
  useEffect(() => {
    (formRef.current?.querySelector(`[id="${comboboxId}"]`) as HTMLInputElement)?.focus();
  }, [selectedUsers]);

  const hasUsers = selectedUsers && selectedUsers.length > 0;

  return (
    <div className="page-interactions__recommendation">
      <Form {...formProps} id="page-recommendation-form" ref={formRef}>
        <h2 className="page-interactions__panel-headline font-bold-15-19">{t('formTitle')}</h2>
        <Row>
          <UserSelectComboBox
            id={comboboxId}
            labelTitle={t('inputs.recipientId.label')}
            suggestUrl={recipients.comboBoxUrl.url}
            validationMessage={hasUsers ? '' : t('validationMessage')}
            modifierInput={FormInputModifier.INVERTED}
            modifierListBox={ListBoxModifier.MAX_HEIGHT_200}
            required
            onSelection={item => {
              if (item && selectedUsers.every(i => i.id !== item.id)) {
                sendLiveRegionMessage(t('personSelected', { name: item.profileName }));
                setSelectedUsers([item, ...selectedUsers]);
              }
            }}
          />
        </Row>
        {hasUsers && (
          <ul className="page-interactions__person-preview-list" aria-label={t('listName')}>
            {selectedUsers.map(user => (
              <li className="page-interactions__person-preview-list-item" key={user.id}>
                <PersonPreview
                  {...user}
                  buttonTooltipPlacement={TooltipPlacement.TOP}
                  reset={id => {
                    setSelectedUsers(selectedUsers.filter(el => el.id !== id));
                  }}
                />
                <HiddenInput key={user.id} name={recipients.name} value={user.id} />
              </li>
            ))}
          </ul>
        )}
        <Row>
          <Textarea label={t('inputs.message.label')} param={message} />
        </Row>
        {/* div needed for correct font color in dark backgrounds */}
        <div className="son-application">
          <SubmitButton
            buttonLabel={t('buttonSendText')}
            messageSuccess={t('status.success')}
            messageSending={t('status.sending')}
            messageFail={t('status.fail')}
            fetchState={fetchState}
            active={fetchState.type !== 'success' && fetchState.type !== 'loading'}
          />
        </div>
      </Form>
    </div>
  );
}
